import React, { useRef, useState } from 'react';
import { Box, TextField, Button, Typography, Stack, Chip, CircularProgress, Link } from '@mui/material';
import { useMsal } from '@azure/msal-react';
import { AI_ENGINE_URL, BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { searchOnenote, getOnenotePageText } from '../../utils/onenoteSearch';
import { searchMail, getMailBody, getConversationText, groepeerPerGesprek, listMailAttachments, getMailAttachmentFile, MAX_BIJLAGE_BYTES } from '../../utils/graphMailSearch';

// Herbruikbaar: één zoekveld dat meeting-notulen + OneNote + e-mail doorzoekt.
// Bij "+ toevoegen" roept 'ie onAddText(tekstblok) aan; onTerm(query) is optioneel
// (bv. om zoektermen te onthouden voor een titel).
const SRC_META = {
  onenote: { label: 'OneNote', bg: '#ede9fe', color: '#6d28d9' },
  email: { label: 'E-mail', bg: '#e0f2fe', color: '#0369a1' },
  meeting: { label: 'Meeting', bg: '#dcfce7', color: '#15803d' },
  gesprek: { label: 'Gesprek', bg: '#e0f2fe', color: '#0369a1' },
  bijlage: { label: 'Bijlage', bg: '#fef3c7', color: '#b45309' },
};
// Wat /Api/parse-attachment aankan; de rest tonen we wel, maar zonder knop.
const PARSEBAAR = /\.(pdf|docx|pptx|txt|md)$/i;

// Zoekresultaten uit mail zijn losse berichten uit dezelfde thread. We tonen
// per gesprek één rij (met de losse treffers uitklapbaar eronder), zodat je in
// één klik de héle conversatie als bron toevoegt in plaats van losse flarden.
function mailRijen(mails) {
  return groepeerPerGesprek(mails).map((groep) => {
    const kids = groep.berichten.map((m) => ({
      source: 'email', id: `mail-${m.id}`, refId: m.id, title: m.subject,
      sub: [m.from, m.received ? String(m.received).slice(0, 10) : ''].filter(Boolean).join(' · '),
      url: m.webLink, subject: m.subject, from: m.from, hasAttachments: m.hasAttachments,
    }));
    if (!groep.conversationId) return kids[0];
    const deelnemers = groep.deelnemers;
    return {
      source: 'email', kind: 'thread', convId: groep.conversationId,
      id: `thr-${groep.conversationId}`, title: groep.onderwerp, subject: groep.onderwerp,
      url: groep.laatste?.webLink || null,
      sub: [
        deelnemers.slice(0, 3).join(', ') + (deelnemers.length > 3 ? ` +${deelnemers.length - 3}` : ''),
        groep.laatste?.received ? String(groep.laatste.received).slice(0, 10) : '',
        `${kids.length} treffer${kids.length === 1 ? '' : 's'} — hele gesprek toevoegen`,
      ].filter(Boolean).join(' · '),
      kids,
    };
  });
}

export default function SourceSearchBox({ onAddText, onTerm, label = 'Zoek in meetings, notulen & e-mail (klantnaam of onderwerp)' }) {
  const { instance } = useMsal();
  const userEmail = instance.getActiveAccount()?.username || '';
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [adding, setAdding] = useState(null);
  const [nogBezig, setNogBezig] = useState({}); // per bron: zoekt nog
  // Per mail-id: 'loading' | [{id,name,size}] — bijlagen worden pas opgehaald
  // als iemand op het paperclipje klikt (scheelt Graph-calls bij elke zoek).
  const [bijlagen, setBijlagen] = useState({});
  // Inline voorbeeld (eerste stuk tekst) — sneller dan OneNote/Outlook openen.
  const [previews, setPreviews] = useState({}); // { [itemId]: 'loading' | tekst }
  // Uitgeklapte gesprekken: dan zie je de losse berichten die matchten.
  const [openThreads, setOpenThreads] = useState({});
  const toggleThread = (id) => setOpenThreads((v) => ({ ...v, [id]: !v[id] }));
  const togglePreview = async (item) => {
    if (previews[item.id] !== undefined) { setPreviews((v) => { const n = { ...v }; delete n[item.id]; return n; }); return; }
    setPreviews((v) => ({ ...v, [item.id]: 'loading' }));
    try {
      let tekst = '';
      if (item.kind === 'thread') tekst = (await getConversationText(item.convId, { maxChars: 4000 })).tekst;
      else if (item.source === 'onenote') tekst = await getOnenotePageText(item.refId);
      else if (item.source === 'email') tekst = await getMailBody(item.refId);
      else {
        const res = await authFetch(`${AI_ENGINE_URL}/api/v1/meetings/${item.refId}`);
        const full = res.ok ? await res.json() : {};
        tekst = full.summary || (full.transcript || '').slice(0, 2000);
      }
      setPreviews((v) => ({ ...v, [item.id]: String(tekst || '(leeg)').slice(0, 1500) }));
    } catch (e) { setPreviews((v) => ({ ...v, [item.id]: `Voorbeeld laden mislukt: ${e.message}` })); }
  };

  const searchMeetings = async (q) => {
    const url = `${AI_ENGINE_URL}/api/v1/meetings/?search=${encodeURIComponent(q)}${userEmail ? `&user_email=${encodeURIComponent(userEmail)}` : ''}`;
    const res = await authFetch(url);
    if (!res.ok) throw new Error(`meetings ${res.status}`);
    const d = await res.json();
    return Array.isArray(d) ? d : (d.meetings || d.results || []);
  };
  const meetingDate = (m) => m.meeting_date || m.date || m.created_at || null;
  const meetingTitle = (m) => m.title || m.subject || m.name || (meetingDate(m) ? `Meeting ${String(meetingDate(m)).slice(0, 10)}` : `Meeting ${m.id}`);

  const zoekSeqRef = useRef(0);
  const runSearch = async () => {
    const q = query.trim();
    if (!q) return;
    if (onTerm) onTerm(q);
    const seq = ++zoekSeqRef.current;
    setLoading(true); setErr(null); setResults([]); setBijlagen({}); setPreviews({}); setOpenThreads({});
    setNogBezig({ meetings: true, mail: true, onenote: true });
    const errs = [];
    const klaar = (bron) => setNogBezig((v) => ({ ...v, [bron]: false }));
    const voegToe = (items) => { if (seq === zoekSeqRef.current && items.length) setResults((prev) => [...(prev || []), ...items]); };

    // Progressief: elke bron toont zijn treffers zodra hij klaar is — meetings
    // (eigen database) in <1s, mail erna, OneNote als laatste (Graph is traag).
    const taken = [
      searchMeetings(q).then((v) => voegToe((v || []).slice(0, 15).map(m => ({ source: 'meeting', id: `mtg-${m.id}`, refId: m.id, title: meetingTitle(m), sub: meetingDate(m) ? String(meetingDate(m)).slice(0, 10) : '', url: null }))))
        .catch((e) => errs.push(`Meetings: ${e.message || 'mislukt'}`)).finally(() => klaar('meetings')),
      searchMail(q).then((v) => voegToe(mailRijen(v || [])))
        .catch((e) => errs.push(`E-mail: ${e.message || 'mislukt'}`)).finally(() => klaar('mail')),
      searchOnenote(q).then((v) => voegToe((v || []).map(p => ({ source: 'onenote', id: `on-${p.id}`, refId: p.id, title: p.title, sub: p.modified ? String(p.modified).slice(0, 10) : '', url: p.webUrl }))))
        .catch((e) => errs.push(`OneNote: ${e.message || 'mislukt'}`)).finally(() => klaar('onenote')),
    ];
    await Promise.allSettled(taken);
    if (seq !== zoekSeqRef.current) return;
    setErr(errs.length ? errs.join(' · ') : null);
    setLoading(false);
    setNogBezig({ meetings: false, mail: false, onenote: false });
  };

  const toggleBijlagen = async (item) => {
    if (bijlagen[item.id]) { setBijlagen(prev => { const n = { ...prev }; delete n[item.id]; return n; }); return; }
    setBijlagen(prev => ({ ...prev, [item.id]: 'loading' }));
    try {
      const lijst = await listMailAttachments(item.refId);
      setBijlagen(prev => ({ ...prev, [item.id]: lijst }));
    } catch (e) {
      setErr(`Bijlagen: ${e.message}`);
      setBijlagen(prev => { const n = { ...prev }; delete n[item.id]; return n; });
    }
  };

  const addBijlage = async (item, att) => {
    setAdding(`att-${att.id}`); setErr(null);
    try {
      const file = await getMailAttachmentFile(item.refId, att.id);
      const fd = new FormData();
      fd.append('file', file, file.name);
      const r = await authFetch(`${BACKEND_URL}/Api/parse-attachment`, { method: 'POST', body: fd });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `parse ${r.status}`);
      await Promise.resolve(onAddText(`\n\n— Uit bijlage: ${file.name} (bij e-mail: ${item.subject}) —\n${d.text || ''}`.slice(0, 12000)));
    } catch (e) { setErr(`Bijlage ${att.name}: ${e.message}`); } finally { setAdding(null); }
  };

  const addItem = async (item) => {
    setAdding(item.id); setErr(null);
    try {
      let block;
      if (item.kind === 'thread') {
        const { aantal, tekst } = await getConversationText(item.convId);
        block = `\n\n— Uit e-mailgesprek: ${item.subject} (${aantal} bericht${aantal === 1 ? '' : 'en'}, chronologisch) —\n${tekst}`;
      } else if (item.source === 'onenote') {
        const text = await getOnenotePageText(item.refId);
        block = `\n\n— Uit OneNote-notulen: ${item.title} —\n${text}`.slice(0, 8000);
      } else if (item.source === 'meeting') {
        const res = await authFetch(`${AI_ENGINE_URL}/api/v1/meetings/${item.refId}`);
        const full = res.ok ? await res.json() : {};
        const text = [full.summary, full.transcript].filter(Boolean).join('\n\n').slice(0, 12000);
        block = `\n\n— Uit meeting-notulen: ${item.title} —\n${text}`;
      } else {
        const text = await getMailBody(item.refId);
        block = `\n\n— Uit e-mail: ${item.subject}${item.from ? ` (van ${item.from})` : ''} —\n${text}`.slice(0, 8000);
      }
      await Promise.resolve(onAddText(block)); // async handlers (projectnotitie) afwachten — anders dubbelklik = dubbele POST
    } catch (e) { setErr(e.message); } finally { setAdding(null); }
  };

  // Eén resultaatrij (gesprek, losse mail, meeting of notulen).
  const basisRij = (it, ml) => {
    const m = SRC_META[it.kind === 'thread' ? 'gesprek' : it.source] || { label: 'Bron', bg: '#f1f5f9', color: '#475569' };
    return (
      <Stack key={it.id} direction="row" spacing={1} alignItems="center"
        sx={{ bgcolor: '#fff', border: '1px solid #eee', borderRadius: 1, px: 1, py: 0.5, ml }}>
        <Chip size="small" label={m.label} sx={{ flexShrink: 0, height: 20, fontSize: 10, fontWeight: 700, bgcolor: m.bg, color: m.color }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" noWrap>{it.title}</Typography>
          {it.sub && <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{it.sub}</Typography>}
        </Box>
        {it.kind === 'thread' && (
          <Typography variant="caption" onClick={() => toggleThread(it.id)}
            sx={{ color: '#64748b', cursor: 'pointer', fontWeight: 700, flexShrink: 0 }}
            title="De losse berichten uit dit gesprek die op je zoekterm matchten">
            {openThreads[it.id] ? 'verberg berichten' : `berichten (${it.kids.length})`}
          </Typography>
        )}
        {it.hasAttachments && (
          <Button size="small" onClick={() => toggleBijlagen(it)} sx={{ minWidth: 0, px: 0.75 }}
            title="Bijlagen tonen — pdf/Word/PowerPoint kun je als brontekst toevoegen">
            {bijlagen[it.id] === 'loading' ? <CircularProgress size={14} /> : '📎'}
          </Button>
        )}
        <Typography variant="caption" onClick={() => togglePreview(it)}
          sx={{ color: '#6366f1', cursor: 'pointer', fontWeight: 700, flexShrink: 0 }}>
          {previews[it.id] !== undefined ? 'verberg' : 'voorbeeld'}
        </Typography>
        {it.url && <Link href={it.url} target="_blank" rel="noopener noreferrer" variant="caption">openen</Link>}
        <Button size="small" onClick={() => addItem(it)} disabled={adding !== null} sx={{ flexShrink: 0 }}>
          {adding === it.id ? <CircularProgress size={14} /> : (it.kind === 'thread' ? '+ hele gesprek' : '+ toevoegen')}
        </Button>
      </Stack>
    );
  };

  const previewRij = (it, ml) => (previews[it.id] === undefined ? [] : [(
    <Box key={`${it.id}-prev`} sx={{ ml: ml + 3, px: 1.5, py: 1, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 1, maxHeight: 220, overflowY: 'auto' }}>
      {previews[it.id] === 'loading'
        ? <CircularProgress size={14} />
        : <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap', color: '#334155' }}>{previews[it.id]}</Typography>}
    </Box>
  )]);

  const bijlageRijen = (it, ml) => {
    const lijst = bijlagen[it.id];
    if (!Array.isArray(lijst)) return [];
    if (!lijst.length) return [(
      <Typography key={`${it.id}-leeg`} variant="caption" color="text.secondary" sx={{ pl: ml + 4 }}>
        Geen bijlagen om toe te voegen (inline afbeeldingen en cloud-links tellen niet mee).
      </Typography>
    )];
    return lijst.map((att) => {
      const m = SRC_META.bijlage;
      const teGroot = (att.size || 0) > MAX_BIJLAGE_BYTES;
      const kan = att.isFile !== false && PARSEBAAR.test(att.name) && !teGroot;
      return (
        <Stack key={`att-${att.id}`} direction="row" spacing={1} alignItems="center"
          sx={{ bgcolor: '#fffbeb', border: '1px dashed #fcd34d', borderRadius: 1, px: 1, py: 0.5, ml: ml + 3 }}>
          <Chip size="small" label={m.label} sx={{ flexShrink: 0, height: 20, fontSize: 10, fontWeight: 700, bgcolor: m.bg, color: m.color }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" noWrap>{att.name}</Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {att.size ? `${Math.max(1, Math.round(att.size / 1024))} kB` : ''}
              {att.isFile === false ? ' · mail/afspraak als bijlage — niet als bron toe te voegen'
                : teGroot ? ' · te groot (max 25 MB)'
                  : !kan ? ' · alleen pdf/Word/PowerPoint/tekst kan als bron' : ''}
            </Typography>
          </Box>
          <Button size="small" onClick={() => addBijlage(it, att)} disabled={!kan || adding !== null}>
            {adding === `att-${att.id}` ? <CircularProgress size={14} /> : '+ toevoegen'}
          </Button>
        </Stack>
      );
    });
  };

  // Rij + voorbeeld + bijlagen, en bij een uitgeklapt gesprek ook de losse
  // berichten eronder (ingesprongen, met hun eigen bijlagen).
  const rijenVoor = (it, ml = 0) => {
    const rijen = [basisRij(it, ml), ...previewRij(it, ml), ...bijlageRijen(it, ml)];
    if (it.kind === 'thread' && openThreads[it.id]) {
      it.kids.forEach((kid) => rijen.push(...rijenVoor(kid, ml + 3)));
    }
    return rijen;
  };

  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField size="small" fullWidth label={label} value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); runSearch(); } }} />
        <Button size="small" variant="outlined" onClick={runSearch} disabled={loading || !query.trim()}>
          {loading ? <CircularProgress size={16} /> : 'Zoek'}
        </Button>
      </Stack>
      {err && <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>{err}</Typography>}
      {loading && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
          <CircularProgress size={11} />
          {['meetings', 'mail', 'onenote'].filter((b) => nogBezig[b]).map((b) => (b === 'onenote' ? 'OneNote (duurt het langst)' : b)).join(' · ') || 'afronden…'}
        </Typography>
      )}
      {results && !results.length && !err && !loading && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>Niets gevonden in meetings, notulen of e-mail.</Typography>
      )}
      {results && results.length > 0 && (
        <Stack spacing={0.5} sx={{ mt: 1, maxHeight: 260, overflowY: 'auto' }}>
          {results.flatMap((it) => rijenVoor(it))}
        </Stack>
      )}
    </Box>
  );
}
